import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, Loader2, PackageX, Tag, Store, RotateCcw } from "lucide-react";

const PERIODS = [
  { v: 30, l: "30 days" },
  { v: 90, l: "90 days" },
  { v: 365, l: "1 year" },
];

function Bars({ rows, labelKey, valueKey, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  if (rows.length === 0) return <p className="text-sm text-slate-400 py-2">No data yet.</p>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={`${r[labelKey]}-${i}`} className="flex items-center gap-3">
          <div className="w-40 shrink-0 truncate text-sm font-medium text-slate-700" title={String(r[labelKey])}>{r[labelKey] || "—"}</div>
          <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(r[valueKey] / max) * 100}%` }} />
          </div>
          <div className="w-12 shrink-0 text-right text-sm font-bold tabular-nums text-slate-800">{r[valueKey]}</div>
        </div>
      ))}
    </div>
  );
}

export default function ReturnsAnalytics() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/returns/analytics?days=${days}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="returns-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
          <BarChart3 className="w-5 h-5 text-orange-500" /> Returns insights <span className="text-sm font-normal text-slate-400">· all brands</span>
        </h3>
        <div className="flex gap-1.5" data-testid="returns-analytics-period">
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setDays(p.v)} data-testid={`ra-period-${p.v}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${days === p.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{p.l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading insights…</div>
      ) : !data ? (
        <p className="text-sm text-red-600 py-4">Couldn&apos;t load analytics.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Stat icon={RotateCcw} label="Returns" value={data.total_returns} />
            <Stat icon={PackageX} label="Units returned" value={data.total_units} />
            <Stat icon={Tag} label="Distinct SKUs" value={data.top_skus.length} />
            <Stat icon={Store} label="Brands" value={data.top_brands.length} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Top returned products (units)</p>
              <Bars rows={data.top_skus.map((s) => ({ label: s.name || s.sku, units: s.units }))} labelKey="label" valueKey="units" testid="ra-top-skus" />
            </div>
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Top reasons</p>
                <Bars rows={data.top_reasons.map((r) => ({ label: r.reason, count: r.count }))} labelKey="label" valueKey="count" testid="ra-top-reasons" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Most common conditions</p>
                <div className="flex flex-wrap gap-2" data-testid="ra-top-conditions">
                  {data.top_condition_tags.length === 0 ? <span className="text-sm text-slate-400">No condition data yet.</span> :
                    data.top_condition_tags.map((t) => (
                      <span key={t.tag} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-1 rounded-full">
                        {t.label} <span className="text-xs font-bold text-orange-600">{t.units}</span>
                      </span>
                    ))}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Top brands by returns</p>
                <Bars rows={data.top_brands.map((b) => ({ label: b.brand, units: b.units }))} labelKey="label" valueKey="units" testid="ra-top-brands" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 text-slate-400 text-xs font-semibold uppercase tracking-wider"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <p className="text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{value}</p>
    </div>
  );
}
